import {
  IPackageList,
  IPackages,
  IRawInquiriesResData,
  IRawInquiryItem,
  IRawPackagesResData,
  servicesCategoriesKeys,
} from "@/interfaces";

const CATEGORY_MAP: Record<number, servicesCategoriesKeys> = {
  1: "banking",
  2: "vehicle",
  3: "government",
  4: "legal",
};

export const getPackagesAdapter = (
  data: IRawPackagesResData | undefined | null,
  allInquiries?: IRawInquiriesResData | IRawInquiryItem[] | null,
): { list: IPackages[]; user: any } => {
  if (!Array.isArray(data?.list)) return { list: [], user: data?.user };

  const packagesList: IPackageList[] = data.list;
  const inquiriesArray: IRawInquiryItem[] = Array.isArray(allInquiries)
    ? allInquiries
    : allInquiries && typeof allInquiries === "object"
      ? (
          Object.values(
            allInquiries as IRawInquiriesResData,
          ) as IRawInquiryItem[][]
        ).flat()
      : [];

  const inquiryIdToName = new Map<number, string>();
  const inquiryIdToPrice = new Map<number, number>();

  for (const inq of inquiriesArray) {
    const id = Number(inq.id);
    inquiryIdToName.set(id, inq.name);
    const price = Number((inq as any).price_user ?? 0);
    if (!Number.isNaN(price) && price > 0) {
      inquiryIdToPrice.set(id, price);
    }
  }
  const purchasedPackageIds = new Set(
    (data.purchased || []).map((p) => String(p.campaign_id)),
  );
  // Filter only active packages before sorting
  const activePackages = packagesList.filter((pkg) => pkg.is_active);

  // Sort packages by ascending numeric amount (price)
  const sortedData = [...activePackages].sort(
    (a, b) => Number(a.amount) - Number(b.amount),
  );

  const list: IPackages[] = sortedData.map((pkg) => {
    const duration = `${pkg.day} روز | ${pkg.count} استعلام`;

    const perInquiry =
      pkg.count && pkg.count > 0
        ? Number(pkg.amount) / Number(pkg.count)
        : undefined;

    // Compute reference price from the included services' prices (average)
    const serviceIds = Array.isArray(pkg.services)
      ? pkg.services.map((s) => Number(s.service))
      : [];
    const refPrices = serviceIds
      .map((id) => inquiryIdToPrice.get(id))
      .filter(
        (v): v is number => typeof v === "number" && !Number.isNaN(v) && v > 0,
      );

    const referenceInquiryPrice = refPrices.length
      ? Math.round(refPrices.reduce((a, b) => a + b, 0) / refPrices.length)
      : undefined;

    let discount: string | undefined;
    let priceWithoutDiscount: number | undefined;
    if (referenceInquiryPrice && perInquiry) {
      const diff = referenceInquiryPrice - perInquiry;
      const pct = Math.round((diff / referenceInquiryPrice) * 100);
      discount = pct > 0 ? `${pct}%` : undefined;

      priceWithoutDiscount = Number(pkg.count) * referenceInquiryPrice;
    }

    const categories: servicesCategoriesKeys[] = Array.isArray(pkg.services)
      ? Array.from(
          new Set(
            pkg.services
              .map((s) => CATEGORY_MAP[Number(s.inquery_category)])
              .filter(Boolean) as servicesCategoriesKeys[],
          ),
        )
      : [];
    const isPurchased = purchasedPackageIds.has(String(pkg.id));
    return {
      id: String(pkg.id),
      title: pkg.name,
      duration,
      price: String(pkg.amount),
      color: pkg.color,
      discount,
      campaign_id: pkg.campaign_id,
      inquiryPrice: priceWithoutDiscount,
      perInquiryPrice: perInquiry ? String(Math.round(perInquiry)) : undefined,
      services: Array.isArray(pkg.services)
        ? (pkg.services
            .map(
              (s) => inquiryIdToName.get(Number(s.service)) || s.inquery_name,
            )
            .filter(Boolean) as string[])
        : [],
      categories,
      purchased: isPurchased,
      serviceIds,
      is_active: pkg.is_active,
    };
  });
  let newUsers = data?.user;
  if (data?.user && data?.user.length > 0) {
    newUsers = data?.user.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  }
  return {
    list,
    user: newUsers,
  };
};
